'use client';

/**
 * Full-height mobile-app promo — a copy column (store links, big headline,
 * description, scan-to-download QR) beside a glass card that a hand-held
 * phone rises out of, with concentric rings drawn behind it, over a dark
 * background lit by a navy glow on the left.
 *
 * <pre>
 *   ┌──────────────────────────────────────────────────────────┐
 *   │░glow░                                 ▄▄▄                │
 *   │  App Store  Google Play             ┌─█ █─────────────┐  │
 *   │  Her an,                            │ █ █  phone      │  │
 *   │  her yerde                          │ ███  in hand    │  │
 *   │  öğren !                            │  ◜ ◠◠◠◠◠ ◝      │  │
 *   │  Description, two lines             │ ◜  rings   ◝    │  │
 *   │  ▣ QR                               └─────────────────┘  │
 *   └──────────────────────────────────────────────────────────┘
 * </pre>
 *
 * <p>Sizes follow the design at 1920px: an 868×482 card and a 718×604 phone
 * image anchored to the card's bottom edge, so its top 122px rise above the
 * card. Both scale down together with the stage. The store marks and the QR
 * share one colour ({@code #969696}); the description is Inter Regular 18px
 * in the same grey.
 *
 * <p>The QR is generated from {@code qrValue} (usually the download link) and
 * drawn as an SVG path, so it takes the icon colour at any size. It is hidden
 * on phones — nobody scans the screen they're holding.
 *
 * <p>Full height comes from {@link FullHeightSlider} (as a single slide), and
 * the glow is its backdrop.
 */
import { useId, useMemo, type CSSProperties, type ReactNode } from 'react';
import { PortalLink, useBlockPart } from '@akademi/site-builder-runtime';
import { tx } from '../_shared/coerce';
import { resizeImageUrl } from '../_shared/resizeImageUrl';
import { FullHeightFrame, FullHeightSlider } from '../_shared/FullHeightSlider';
import { encodeQr, qrPath } from '../_shared/qrCode';

type Localized = string | Record<string, string>;

export interface FullHeightMobilePromoProps {
  /** Line breaks are kept. */
  title?: Localized;
  description?: Localized;

  appStoreLabel?: Localized;
  /** Small line above the store name, e.g. "Hemen indirin". */
  appStoreSubLabel?: Localized;
  appStoreHref?: string;
  playStoreLabel?: Localized;
  playStoreSubLabel?: Localized;
  playStoreHref?: string;
  /** Open the store links in a new tab. */
  storeLinksNewTab?: boolean;

  /** Text or link encoded in the QR code. Empty = no QR. */
  qrValue?: string;
  /** Accessible name of the QR code. */
  qrLabel?: Localized;

  /** Hand-and-phone image, drawn 718×604 at full size (transparent PNG/WebP). */
  image?: string;
  imageAlt?: Localized;
  showRings?: boolean;
  ringColor?: string;

  titleTag?: 'h1' | 'h2';
  /** CSS font family for the headline. Empty = the theme's display font. */
  titleFontFamily?: string;
  titleFontSize?: number;
  titleFontWeight?: string;
  titleColor?: string;
  /** CSS font family for the description and store marks. Empty = the theme's font. */
  fontFamily?: string;
  descriptionFontSize?: number;
  descriptionColor?: string;
  /** Store marks and the QR code. */
  iconColor?: string;

  backgroundColor?: string;
  showGlow?: boolean;
  glowColor?: string;
}

// Rhythm, stage geometry and breakpoints live here — inline styles can't
// express them. The stage is the card plus the 122px the phone rises above
// it (868 × 604 at full size); percentages keep card, image and rings
// locked together as it shrinks.
const FULL_HEIGHT_MOBILE_PROMO_CSS = `
.fh-slider-frame.fhmp-frame { padding-inline: 24px; }
.fhmp-layout {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 64px;
  width: 100%;
  max-width: 1470px;
  margin: 0 auto;
}
.fhmp-copy { flex: 0 0.3 440px; min-width: 0; }
.fhmp-stores { display: flex; flex-wrap: wrap; align-items: center; gap: 12px 30px; }
.fhmp-store {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: inherit;
  text-decoration: none;
  transition: opacity 150ms ease;
}
a.fhmp-store:hover { opacity: 0.8; }
a.fhmp-store:focus-visible, .fhmp-qr:focus-visible { outline: 2px solid rgba(255, 255, 255, 0.85); outline-offset: 4px; border-radius: 4px; }
.fhmp-title { margin: 44px 0 0; font-size: var(--fhmp-title-size); line-height: 0.85; letter-spacing: -0.01em; }
.fhmp-desc { max-width: 440px; margin: 60px 0 0; font-size: var(--fhmp-desc-size); line-height: 1.28; }
.fhmp-copy > :first-child { margin-top: 0; }
.fhmp-qr { display: block; width: 72px; height: 72px; margin-top: 72px; }
.fhmp-stage { position: relative; flex: 0 1 868px; min-width: 0; aspect-ratio: 868 / 604; }
.fhmp-card {
  position: absolute;
  inset: 20.2% 0 0 0;
  overflow: hidden;
  box-sizing: border-box;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.04);
}
.fhmp-rings { position: absolute; inset: 0; width: 100%; height: 100%; }
.fhmp-image {
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 82.72%;
  height: 100%;
  max-width: none;
  transform: translateX(-50%);
  object-fit: contain;
  object-position: center bottom;
}
@media (max-width: 1279px) {
  .fhmp-layout { gap: 40px; }
  .fhmp-title { font-size: calc(var(--fhmp-title-size) * 0.8); }
}
@media (max-width: 1023px) {
  .fhmp-layout { flex-direction: column; align-items: stretch; gap: 48px; }
  .fhmp-copy { flex: none; }
  .fhmp-title, .fhmp-desc { margin-top: 32px; }
  .fhmp-qr { margin-top: 40px; }
  .fhmp-stage { flex: none; width: 100%; max-width: 868px; margin: 0 auto; }
}
@media (max-width: 767px) {
  .fh-slider-frame.fhmp-frame { padding-inline: 16px; }
  .fhmp-title { font-size: calc(var(--fhmp-title-size) * 0.66); margin-top: 28px; }
  .fhmp-desc { font-size: calc(var(--fhmp-desc-size) * 0.89); margin-top: 24px; }
  .fhmp-qr { display: none; }
}
@media (prefers-reduced-motion: reduce) {
  .fhmp-store { transition: none; }
}
`;

/** Keeps an author-typed family inside its declaration. */
const cleanFamily = (family?: string) => (family ?? '').replace(/[;{}<>]/g, '').trim();

/** Form fields can hand numbers over as strings ("64"). */
const px = (n: unknown, fallback: number) => {
  const v = Number(n);
  return `${Number.isFinite(v) && v > 0 ? v : fallback}px`;
};

const mix = (color: string, percent: number) =>
  `color-mix(in srgb, ${color} ${percent}%, transparent)`;

/**
 * Sampled from the design: a navy wash strongest over the copy column that
 * fades to black two thirds of the way across, a touch brighter along the
 * top and bottom edges than through the middle.
 */
const glowBackground = (color: string) =>
  [
    `radial-gradient(45% 38% at 22% 0%, ${mix(color, 70)} 0%, transparent 100%)`,
    `radial-gradient(45% 38% at 22% 100%, ${mix(color, 90)} 0%, transparent 100%)`,
    `linear-gradient(90deg, ${mix(color, 70)} 0%, ${mix(color, 76)} 18%, ${mix(color, 62)} 30%, ` +
      `${mix(color, 45)} 38%, ${mix(color, 26)} 46%, ${mix(color, 12)} 55%, ${mix(color, 4)} 65%, ` +
      `transparent 78%)`,
  ].join(', ');

export function FullHeightMobilePromo({
  title,
  description,
  appStoreLabel,
  appStoreSubLabel,
  appStoreHref,
  playStoreLabel,
  playStoreSubLabel,
  playStoreHref,
  storeLinksNewTab = true,
  qrValue,
  qrLabel,
  image,
  imageAlt,
  showRings = true,
  ringColor = '#4292da',
  titleTag = 'h2',
  titleFontFamily,
  titleFontSize = 64,
  titleFontWeight = '700',
  titleColor = '#ffffff',
  fontFamily = 'Inter',
  descriptionFontSize = 18,
  descriptionColor = '#969696',
  iconColor = '#969696',
  backgroundColor = '#000000',
  showGlow = true,
  glowColor = '#111a33',
}: FullHeightMobilePromoProps) {
  const part = useBlockPart();

  const titleText = tx(title);
  const descriptionText = tx(description);
  const qrText = (qrValue ?? '').trim();
  const qr = useMemo(() => (qrText ? encodeQr(qrText, 'M') : null), [qrText]);
  const stores = [
    {
      kind: 'apple' as const,
      label: tx(appStoreLabel),
      subLabel: tx(appStoreSubLabel),
      href: appStoreHref,
    },
    {
      kind: 'google' as const,
      label: tx(playStoreLabel),
      subLabel: tx(playStoreSubLabel),
      href: playStoreHref,
    },
  ].filter((s) => s.label);

  const hasContent = Boolean(titleText || descriptionText || stores.length || qr || image);
  const TitleTag = titleTag === 'h1' ? 'h1' : 'h2';

  const bodyFamily = cleanFamily(fontFamily)
    ? `${cleanFamily(fontFamily)}, var(--font-family-body, sans-serif)`
    : 'var(--font-family-body, inherit)';
  const headlineFamily = cleanFamily(titleFontFamily)
    ? `${cleanFamily(titleFontFamily)}, var(--font-family-display, sans-serif)`
    : 'var(--font-family-display, inherit)';

  const typeVars = {
    '--fhmp-title-size': px(titleFontSize, 64),
    '--fhmp-desc-size': px(descriptionFontSize, 18),
  } as CSSProperties;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: FULL_HEIGHT_MOBILE_PROMO_CSS }} />
      <FullHeightSlider
        slides={hasContent ? [0] : []}
        background={backgroundColor}
        backdrop={
          showGlow ? (
            <div
              style={{ position: 'absolute', inset: 0, background: glowBackground(glowColor) }}
            />
          ) : null
        }
        renderSlide={(_, __, { hasControls }) => (
          <FullHeightFrame
            hasControls={hasControls}
            className="fhmp-frame"
            style={{
              alignItems: 'center',
              // The design sits the card 35px above true centre; the extra
              // bottom padding lifts it by half of that.
              paddingBottom: (hasControls ? 80 : 40) + 70,
              ...typeVars,
            }}
          >
            <div className="fhmp-layout">
              <div className="fhmp-copy" style={{ fontFamily: bodyFamily }}>
                {stores.length > 0 ? (
                  <div className="fhmp-stores" style={{ color: iconColor }}>
                    {stores.map((store, i) => (
                      <StoreMark
                        key={store.kind}
                        {...part(i === 0 ? 'cta' : 'cta-secondary', i)}
                        {...store}
                        newTab={storeLinksNewTab}
                      />
                    ))}
                  </div>
                ) : null}
                {titleText ? (
                  <TitleTag
                    {...part('title')}
                    className="fhmp-title"
                    style={{
                      fontFamily: headlineFamily,
                      fontWeight: Number(titleFontWeight) || 700,
                      color: titleColor,
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {titleText}
                  </TitleTag>
                ) : null}
                {descriptionText ? (
                  <p
                    {...part('subtitle')}
                    className="fhmp-desc"
                    style={{ fontWeight: 400, color: descriptionColor, whiteSpace: 'pre-line' }}
                  >
                    {descriptionText}
                  </p>
                ) : null}
                {qr ? (
                  <svg
                    {...part('qr')}
                    className="fhmp-qr"
                    role="img"
                    aria-label={tx(qrLabel) || qrText}
                    viewBox={`0 0 ${qr.length} ${qr.length}`}
                    shapeRendering="crispEdges"
                  >
                    <path d={qrPath(qr)} fill={iconColor} />
                  </svg>
                ) : null}
              </div>
              <div className="fhmp-stage" {...part('media')}>
                <div className="fhmp-card">{showRings ? <Rings color={ringColor} /> : null}</div>
                {image ? (
                  <img
                    className="fhmp-image"
                    src={resizeImageUrl(image, '1436x0')}
                    alt={tx(imageAlt)}
                    width={718}
                    height={604}
                    loading="lazy"
                    decoding="async"
                  />
                ) : null}
              </div>
            </div>
          </FullHeightFrame>
        )}
      />
    </>
  );
}

// =============================================================================
// Card decoration
// =============================================================================

/**
 * Four concentric rings centred just under the card's bottom edge, measured
 * from the design: a bright one, a dim inner one and two faint outer ones.
 * Drawn in the card's own 868×482 space and clipped by it.
 */
function Rings({ color }: { color: string }) {
  const cx = 439;
  const cy = 509;
  return (
    <svg
      className="fhmp-rings"
      viewBox="0 0 868 482"
      preserveAspectRatio="xMidYMax slice"
      aria-hidden="true"
      fill="none"
    >
      <circle cx={cx} cy={cy} r={421} stroke="#ffffff" strokeOpacity={0.04} strokeWidth={4} />
      <circle cx={cx} cy={cy} r={363} stroke={color} strokeOpacity={0.1} strokeWidth={5} />
      <circle cx={cx} cy={cy} r={322} stroke={color} strokeWidth={2} />
      <circle cx={cx} cy={cy} r={280} stroke={color} strokeOpacity={0.3} strokeWidth={2} />
    </svg>
  );
}

// =============================================================================
// Store marks
// =============================================================================

function StoreMark({
  kind,
  label,
  subLabel,
  href,
  newTab,
  ...attrs
}: {
  kind: 'apple' | 'google';
  label: string;
  subLabel: string;
  href?: string;
  newTab: boolean;
}) {
  const inner: ReactNode = (
    <>
      {kind === 'apple' ? <AppleGlyph /> : <GooglePlayGlyph />}
      <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
        {subLabel ? (
          <span style={{ fontSize: 10, fontWeight: 400, lineHeight: '11px' }}>{subLabel}</span>
        ) : null}
        <span
          style={{
            fontSize: 18,
            fontWeight: 500,
            lineHeight: '19px',
            letterSpacing: '-0.01em',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
      </span>
    </>
  );

  if (!href) {
    return (
      <span {...attrs} className="fhmp-store">
        {inner}
      </span>
    );
  }
  return (
    <PortalLink
      {...attrs}
      href={href}
      className="fhmp-store"
      aria-label={`${subLabel} ${label}`.trim()}
      {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {inner}
    </PortalLink>
  );
}

function AppleGlyph() {
  return (
    <svg
      aria-hidden="true"
      width="21"
      height="25"
      viewBox="3.3 2.8 16.6 19.6"
      fill="currentColor"
      style={{ flex: 'none' }}
    >
      <path d="M16.498 12.78c-.022-2.34 1.91-3.46 1.998-3.515-1.087-1.587-2.776-1.804-3.376-1.83-1.435-.146-2.806.844-3.535.844-.74 0-1.86-.823-3.06-.8-1.572.024-3.024.913-3.83 2.318-1.642 2.835-.418 7.024 1.182 9.32.78 1.124 1.708 2.382 2.92 2.337 1.179-.047 1.62-.757 3.04-.757 1.41 0 1.81.757 3.05.732 1.262-.022 2.062-1.142 2.83-2.273.892-1.302 1.258-2.575 1.279-2.642-.028-.012-2.45-.94-2.475-3.732zM14.7 5.84c.65-.787 1.087-1.88.969-2.967-.937.037-2.07.624-2.74 1.408-.6.694-1.124 1.806-.985 2.872 1.044.082 2.108-.532 2.756-1.313z" />
    </svg>
  );
}

/** The monochrome Play mark: a filled triangle split by two crossing cuts. */
function GooglePlayGlyph() {
  const maskId = `fhmp-play-${useId().replace(/:/g, '')}`;
  return (
    <svg aria-hidden="true" width="23" height="25" viewBox="0 0 23 25" style={{ flex: 'none' }}>
      <defs>
        <mask id={maskId}>
          <path
            d="M2.2 1.2 21.3 12.5 2.2 23.8Z"
            fill="#ffffff"
            stroke="#ffffff"
            strokeWidth={2.2}
            strokeLinejoin="round"
          />
          <path d="M1.4 0.6 16.4 16.2M1.4 24.4 16.4 8.8" stroke="#000000" strokeWidth={1.3} />
        </mask>
      </defs>
      <rect width="23" height="25" fill="currentColor" mask={`url(#${maskId})`} />
    </svg>
  );
}
